import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box, TextField, IconButton, Chip, Typography, Paper, Popover, Checkbox,
  FormControlLabel, Button, InputAdornment, CircularProgress,
} from '@mui/material';
import {
  KeyboardArrowRight, KeyboardArrowDown, ArrowUpward, ArrowDownward, FilterList,
  DragIndicator, Search as SearchIcon, Refresh as RefreshIcon, Folder as FolderIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  useTable, tableFeatures, flexRender,
  columnFacetingFeature, columnFilteringFeature, columnVisibilityFeature,
  globalFilteringFeature, rowExpandingFeature, rowSortingFeature,
  createExpandedRowModel, createFacetedRowModel, createFacetedUniqueValues,
  createFilteredRowModel, createSortedRowModel,
  filterFns, sortFns,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import IdChip from '../common/IdChip';
import { usePref } from '../../services/prefs';
import { statusOf } from '../../utils/statusTone';
import useAutoFocus from '../../hooks/useAutoFocus';

// Frozen module-level singletons for empty default props (#943,
// react/no-object-type-as-default-prop). A `= []` / `= {}` default builds a NEW
// value on every render, so a memoised child or an effect dependency sees a change
// that never happened.
const EMPTY_ARRAY = Object.freeze([]);

/**
 * RequirementsTreeGrid — TanStack-Table tree-as-grid for the requirements tree.
 *
 * The free (MIT) alternative to MUI X Pro proven in demos/tanstack-treegrid
 * (MUI-PORT.2b), wired to the real requirements data. Delivers configurable
 * sortable columns, an Excel-style column filter, global search, inline edit
 * (double-click, persisted), and drag-and-drop sibling reorder (persisted — GH#42),
 * with row virtualization and the existing custom context menu. Basis for the
 * future full-column "spreadsheet" view (GH#412). (The main navigator tree is
 * RequirementsTree.jsx; the legacy RichTreeView/@mui/x-tree-view was removed in #383.)
 */

// `type` is now first-class (from type_id); fall back to the legacy
// attributes.type only for older payloads/fixtures.
const typeOf = (item) => item?.type || item?.attributes?.type || 'requirement';

// Descriptions are rich (TinyMCE) HTML; show readable text in the grid while the
// raw value is still what gets edited/persisted.
const stripHtml = (s) => String(s ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

const isContainerItem = (item) =>
  (item?.type || item?.attributes?.type) === 'container' ||
  (Array.isArray(item?.children) && item.children.length > 0);

/**
 * Synthetic tree nodes are UI scaffolding, not items: the paginated "Load more…"
 * row (`useTreeState.makeLoadMoreNodes`, `_kind: 'load_more'`, id `loadmore:…`)
 * and the virtual per-project grouping row (`useTreeState.projectNodeId`,
 * `_kind: 'project'`, id `project:…`). Neither has a row in `items`, so neither
 * may ever appear in a `sibling_order` payload — `PUT /items/{id}/move` would be
 * asked to order an id the backend has never heard of.
 *
 * Matched on `_kind`, the discriminator `useTreeState` actually sets, NOT on the
 * id prefix: the prefix is a display detail, and a real item whose id happened to
 * start with `loadmore:` would be silently dropped by a prefix test.
 *
 * WHY THIS MATTERS (adversarial review, F2): a level with more than
 * `TREE_PAGE_SIZE` (500) children ends in a load-more node. Counting it as a
 * sibling made `getSiblingOrder` report the LAST REAL child at
 * `index < siblingIds.length - 1`, so "Move down" rendered ENABLED on it and
 * clicking it emitted an order containing the synthetic id — a write the server
 * rejects or ignores, with no feedback to the user. Both the enable/disable
 * decision and the emitted payload read this list, so filtering here fixes them
 * together and keeps them consistent by construction.
 */
const isSyntheticNode = (node) => node?._kind === 'load_more' || node?._kind === 'project';

/**
 * Plan a drag-and-drop drop. Pure + exported so it can be unit-tested without
 * simulating a dnd-kit pointer drag. Two outcomes:
 *
 *  - **reorder** (`{ kind:'reorder', id, payload:{ sibling_order } }`) — `active`
 *    and `over` share a parent: reorder among siblings.
 *  - **reparent** (`{ kind:'reparent', id, payload:{ new_parent_id, sibling_order } }`)
 *    — different parents: nest `active` into `over` when `over` is a container,
 *    else move it to `over`'s level. The active item is appended to the new
 *    parent's children. Backed by `PUT /items/{id}/move` (GH#42).
 *
 * Returns null for no-ops, unknown ids, dropping onto self, or any move that
 * would create a cycle (into self / a descendant).
 */
export function planDrop(items, activeId, overId) {
  if (!activeId || !overId || activeId === overId) return null;

  const parentOf = new Map();
  const childrenByParent = new Map(); // parentId (null for root) -> [ids]
  const byId = new Map();
  const walk = (nodes, parentId) => {
    // Same exclusion as `getSiblingOrder` and for the same reason: a synthetic
    // node has no server-side row, so it must never reach a `sibling_order`.
    // Dragging a real item past the trailing "Load more…" row on a >500-child
    // level otherwise produced a payload the backend cannot satisfy.
    const ids = (nodes || []).filter((n) => n && !isSyntheticNode(n)).map((n) => n.id);
    if (ids.length) childrenByParent.set(parentId, ids);
    (nodes || []).forEach((n) => {
      if (!n) return;
      byId.set(n.id, n);
      parentOf.set(n.id, parentId);
      if (Array.isArray(n.children) && n.children.length) walk(n.children, n.id);
    });
  };
  walk(items, null);

  if (!parentOf.has(activeId) || !parentOf.has(overId)) return null;
  const activeParent = parentOf.get(activeId) ?? null;
  const overParent = parentOf.get(overId) ?? null;

  // Same parent -> reorder among siblings.
  if (activeParent === overParent) {
    const order = (childrenByParent.get(activeParent) || []).slice();
    const oldIndex = order.indexOf(activeId);
    const newIndex = order.indexOf(overId);
    if (oldIndex < 0 || newIndex < 0) return null;
    order.splice(newIndex, 0, order.splice(oldIndex, 1)[0]);
    return { kind: 'reorder', id: activeId, payload: { sibling_order: order } };
  }

  // Different parent -> re-parent. Nest into `over` if it's a container, else
  // join `over`'s level.
  const newParent = isContainerItem(byId.get(overId)) ? overId : overParent;
  if (newParent === activeId || newParent === activeParent) return null;

  // Reject cycles: the new parent must not be the active item or a descendant.
  let ancestor = newParent;
  while (ancestor != null) {
    if (ancestor === activeId) return null;
    ancestor = parentOf.get(ancestor) ?? null;
  }

  const newSiblings = (childrenByParent.get(newParent) || []).filter((id) => id !== activeId);
  newSiblings.push(activeId); // append into the new parent
  return { kind: 'reparent', id: activeId, payload: { new_parent_id: newParent ?? null, sibling_order: newSiblings } };
}

/**
 * Non-drag equivalent of a same-parent `planDrop` reorder (SC 2.5.7 Dragging
 * Movements, AA — #648): swap `itemId` with its previous/next sibling and
 * return the SAME `{ id, payload:{ sibling_order } }` shape `planDrop`
 * produces for a reorder, so callers write through the identical
 * `moveItem`/`PUT /items/{id}/move` path drag reorder already uses — this is
 * not a second write path. `direction` is `'up'` or `'down'`. Returns null for
 * an unknown id or a swap that would run off either end of the sibling list
 * (first sibling has no "up", last has no "down") — callers should also
 * disable the corresponding menu action in that case rather than relying on
 * this null return alone.
 *
 * `siblingIds` contains REAL items only — synthetic rows (`load_more`, `project`;
 * see `isSyntheticNode`) are excluded, so `index === siblingIds.length - 1` is a
 * trustworthy "this is the last item on the level" test even when a paginated
 * level renders a trailing "Load more…" row. Passing a synthetic id returns null.
 */
export function getSiblingOrder(items, itemId) {
  const parentOf = new Map();
  const childrenByParent = new Map();
  const walk = (nodes, parentId) => {
    // Synthetic nodes are excluded from the SIBLING LIST (they can never be part
    // of a sibling_order) but are still walked for `parentOf`/descendants — a
    // project node's children are perfectly real items that must resolve.
    const ids = (nodes || []).filter((n) => n && !isSyntheticNode(n)).map((n) => n.id);
    if (ids.length) childrenByParent.set(parentId, ids);
    (nodes || []).forEach((n) => {
      if (!n) return;
      parentOf.set(n.id, parentId);
      if (Array.isArray(n.children) && n.children.length) walk(n.children, n.id);
    });
  };
  walk(items, null);

  if (!parentOf.has(itemId)) return null;
  const parentId = parentOf.get(itemId) ?? null;
  const siblingIds = (childrenByParent.get(parentId) || []).slice();
  const index = siblingIds.indexOf(itemId);
  if (index < 0) return null;
  return { siblingIds, index };
}

export function planMoveSibling(items, itemId, direction) {
  const info = getSiblingOrder(items, itemId);
  if (!info) return null;
  const { siblingIds, index } = info;
  const swapIndex = direction === 'up' ? index - 1 : index + 1;
  if (swapIndex < 0 || swapIndex >= siblingIds.length) return null;
  const order = siblingIds.slice();
  [order[index], order[swapIndex]] = [order[swapIndex], order[index]];
  return { id: itemId, payload: { sibling_order: order } };
}

// TanStack Table v9 registers features explicitly — v8's "every feature is always
// on" behaviour is gone, so an API whose feature is not listed here is simply absent
// at runtime (not an error you would see at build time). This list is the exact set
// this grid calls, and nothing more:
//   columnFilteringFeature  -> column.getCanFilter/getFilterValue/setFilterValue, enableColumnFilter
//   globalFilteringFeature  -> the "Search items..." box (globalFilter state)
//   rowSortingFeature       -> header sort toggle, column.getIsSorted
//   rowExpandingFeature     -> row.getCanExpand/getIsExpanded/getToggleExpandedHandler
//   columnFacetingFeature   -> column.getFacetedUniqueValues (the funnel popover's value list)
//   columnVisibilityFeature -> row.getVisibleCells (v9 moved it out of core)
// The row models that were v8 `getXRowModel()` OPTIONS are now factory SLOTS here;
// the core row model is implicit and no longer passed at all. The full `filterFns` /
// `sortFns` registries are registered rather than hand-picked built-ins because v8
// bundled every built-in and `'auto'` resolution (used by every unsorted/unfiltered
// column here) can only pick from what is registered — narrowing the registry would
// silently change which comparator a column auto-selects, i.e. visible sort order.
// Built statically at module scope, as the helper's docs require.
const treeGridFeatures = tableFeatures({
  columnFacetingFeature,
  columnFilteringFeature,
  columnVisibilityFeature,
  globalFilteringFeature,
  rowExpandingFeature,
  rowSortingFeature,
  expandedRowModel: createExpandedRowModel(),
  facetedRowModel: createFacetedRowModel(),
  facetedUniqueValues: createFacetedUniqueValues(),
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  filterFns,
  sortFns,
});

// Excel-style multi-value filter: keep a row if its value is in the selected set.
const multiSelectFilter = (row, columnId, selected) =>
  !selected || selected.length === 0 || selected.includes(row.getValue(columnId));
multiSelectFilter.autoRemove = (val) => !val || val.length === 0;

/** Funnel button → popover with a checkbox per distinct column value. */
function ColumnFilterButton({ column }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const selected = column.getFilterValue() ?? [];
  // Hoisted out of the dependency array: TanStack memoizes `getFacetedUniqueValues()`
  // internally, so the returned Map is the stable identity to key on — it just could not
  // be STATICALLY checked while it was a call expression inline in the array. Same value,
  // same invalidation, now verifiable.
  const facetedUniqueValues = column.getFacetedUniqueValues();
  const uniqueValues = useMemo(
    () => Array.from(facetedUniqueValues.keys()).filter((v) => v != null).sort(),
    [facetedUniqueValues],
  );
  const toggle = (value) => {
    const set = new Set(selected);
    if (set.has(value)) set.delete(value); else set.add(value);
    column.setFilterValue(set.size ? Array.from(set) : undefined);
  };
  const active = selected.length > 0;
  return (
    <>
      <IconButton size="small" data-testid={`filter-btn-${column.id}`} color={active ? 'primary' : 'default'}
        onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}>
        <FilterList sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 1, minWidth: 180 }} data-testid={`filter-popover-${column.id}`}>
          <Typography variant="caption" sx={{ px: 1 }}>Filter {column.id}</Typography>
          {uniqueValues.map((v) => (
            <FormControlLabel key={v} sx={{ display: 'flex', m: 0 }}
              control={<Checkbox size="small" checked={selected.includes(v)} onChange={() => toggle(v)}
                slotProps={{ input: { 'data-testid': `filter-${column.id}-${v}` } }} />}
              label={<span style={{ fontSize: 13 }}>{v}</span>} />
          ))}
          <Button size="small" fullWidth onClick={() => column.setFilterValue(undefined)}
            data-testid={`filter-clear-${column.id}`}>Clear</Button>
        </Box>
      </Popover>
    </>
  );
}
ColumnFilterButton.propTypes = { column: PropTypes.object.isRequired };

/** Double-click-to-edit cell; commits via table.meta.updateData on blur/Enter. */
function EditableCell({ getValue, row, column, table }) {
  const initial = getValue() ?? '';
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initial);
  // #793: swaps this jsx-a11y/no-autofocus site for the shared hook (rather
  // than a plain `autoFocus`) for consistency with the rest of the app's
  // inline editors — see hooks/useAutoFocus.
  const focusRef = useAutoFocus(editing);
  useEffect(() => { setValue(initial); }, [initial]);
  const commit = () => {
    setEditing(false);
    if (value !== initial) table.options.meta.updateData(row.original, column.id, value);
  };
  if (editing) {
    return (
      <TextField size="small" variant="standard" inputRef={focusRef} fullWidth value={value}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
          if (e.key === 'Escape') { setValue(initial); setEditing(false); }
        }}
        slotProps={{ htmlInput: { 'data-testid': `edit-${row.id}-${column.id}` } }} />
    );
  }
  return (
    <Box data-testid={`cell-${row.id}-${column.id}`}
      onDoubleClick={(e) => { e.stopPropagation(); setValue(initial); setEditing(true); }}
      sx={{ cursor: 'text', minHeight: 24, display: 'flex', alignItems: 'center',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}
      title="Double-click to edit">
      {value}
    </Box>
  );
}

/** One grid row, sortable (drag handle) when reordering is allowed. */
function SortableRow({ row, virtualRow, selected, isDropTarget, onSelect, onContextMenu, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
  const dragShift = transform ? `${CSS.Transform.toString(transform)} ` : '';
  const style = {
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    // Virtual offset for the row, plus any active drag translation.
    transform: `${dragShift}translateY(${virtualRow.start}px)`,
  };
  const isSelected = selected.includes(row.id);
  return (
    <Box ref={setNodeRef} style={style} data-testid={`row-${row.id}`} data-rowid={row.id}
      role="row" aria-selected={isSelected} data-drop-target={isDropTarget || undefined}
      onClick={(e) => onSelect(e, row)}
      onContextMenu={(e) => onContextMenu(e, row)}
      sx={{
        display: 'flex', alignItems: 'center', height: virtualRow.size,
        borderBottom: '1px solid', borderColor: 'divider', cursor: 'pointer',
        bgcolor: isSelected ? 'action.selected' : 'background.paper',
        // Re-parent drop target: highlight the container the item would nest into.
        outline: isDropTarget ? '2px solid' : 'none',
        outlineColor: 'primary.main',
        outlineOffset: '-2px',
        '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
      }}>
      <Box sx={{ width: 28, flex: '0 0 28px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
        {/* #793 H: a drag handle IS a button — made native so the platform supplies the
            role, the tab stop and Enter/Space, which is also what dnd-kit's KeyboardSensor
            expects to drive. It was a <span>: eslint could not see through the
            `{...attributes}` spread (dnd-kit sets role/tabIndex at runtime), and neither
            can a browser before hydration. The inline style is a UA reset — a native
            button would otherwise bring its own border, background, padding and font. */}
        <button type="button" {...attributes} {...listeners} data-testid={`drag-${row.id}`}
          onClick={(e) => e.stopPropagation()}
          aria-label="Drag to reorder among siblings"
          style={{
            cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: 'none', background: 'none', padding: 0, color: 'inherit', font: 'inherit',
          }}
          title="Drag to reorder among siblings">
          <DragIndicator fontSize="small" />
        </button>
      </Box>
      {children}
    </Box>
  );
}

const ROW_HEIGHT = 40;

export default function RequirementsTreeGrid({
  items = EMPTY_ARRAY,
  loading = false,
  error = null,
  selected = EMPTY_ARRAY,
  expanded = EMPTY_ARRAY,
  onSelectedChange,
  onExpandedChange,
  onContextMenu,
  onBackgroundContextMenu,
  onUpdateItem,
  onMoveItem,
  onRefresh,
}) {
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const showHumanIds = usePref('showHumanIds');

  // Bridge useTreeState's expanded array <-> TanStack's expanded record.
  const expandedState = useMemo(() => {
    const rec = {};
    (expanded || []).forEach((id) => { rec[id] = true; });
    return rec;
  }, [expanded]);

  const setExpandedState = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(expandedState) : updater;
    // `next === true` means "expand all" — we never trigger it (no expand-all
    // control) and can't enumerate ids here, so ignore it rather than clobber the
    // current expansion to empty.
    if (next === true) return;
    const ids = Object.keys(next).filter((k) => next[k]);
    if (onExpandedChange) onExpandedChange(null, ids);
  }, [expandedState, onExpandedChange]);

  const updateData = useCallback((item, columnId, value) => {
    if (onUpdateItem) onUpdateItem(item.id, { [columnId]: value });
  }, [onUpdateItem]);

  const columns = useMemo(() => [
    {
      accessorKey: 'title', header: 'Requirement', enableColumnFilter: false,
      meta: { flex: '2 1 0', minWidth: 150 },
      cell: ({ row, getValue, column, table }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', pl: `${row.depth * 18}px`, width: '100%', minWidth: 0 }}>
          {row.getCanExpand() ? (
            <IconButton size="small" data-testid={`expand-${row.id}`}
              onClick={(e) => { e.stopPropagation(); row.getToggleExpandedHandler()(); }}>
              {row.getIsExpanded() ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
            </IconButton>
          ) : <Box sx={{ width: 30, flex: '0 0 30px' }} />}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5, color: typeOf(row.original) === 'container' ? 'primary.main' : 'text.secondary' }}>
            {typeOf(row.original) === 'container' ? <FolderIcon fontSize="small" /> : <DescriptionIcon fontSize="small" />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <EditableCell getValue={getValue} row={row} column={column} table={table} />
          </Box>
        </Box>
      ),
    },
    // Accessor stays the raw display_id-or-id string (sorting/filtering rely on
    // it); the cell renders through IdChip so the `showHumanIds` pref (#174)
    // decides friendly-vs-raw inline, with the other form in the tooltip.
    {
      id: 'id', accessorFn: (row) => row.display_id || row.id, header: 'ID', size: 100,
      enableColumnFilter: false, meta: { flex: '0 0 100px', minWidth: 100 },
      cell: ({ row }) => (
        <IdChip id={row.original.id} displayId={row.original.display_id} showHumanIds={showHumanIds} />
      ),
    },
    {
      id: 'type', header: 'Type', size: 110, accessorFn: (row) => typeOf(row),
      filterFn: multiSelectFilter, meta: { flex: '0 0 110px', minWidth: 110 },
      cell: ({ getValue }) => <Chip size="small" label={getValue()} variant="outlined" color={getValue() === 'container' ? 'primary' : 'default'} />,
    },
    // Excel-style filterable, same as `type` — this is how a human finds every
    // agent-proposed archive (#193 review queue, scoped to filterability): filter this
    // column to `Archive proposed` instead of a dedicated review page. The dot uses the
    // SAME tone map as the legacy tree (utils/statusTone) so a status never renders a
    // different colour in one place than another.
    {
      id: 'status', header: 'Status', size: 130,
      accessorFn: (row) => statusOf(row).label,
      filterFn: multiSelectFilter, meta: { flex: '0 0 130px', minWidth: 130 },
      cell: ({ row }) => {
        const st = statusOf(row.original);
        if (st.label === '—') return null;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <span className={`dot ${st.className}`} data-testid={`status-dot-${row.original.id}`} />
            <Typography variant="body2">{st.label}</Typography>
          </Box>
        );
      },
    },
    {
      // Display-only: descriptions are rich TinyMCE HTML — editing them as plain
      // text inline would silently destroy formatting, and they're edited in the
      // ItemDetails panel (TinyMCE). The accessor returns stripped text so global
      // search matches the *visible* words, not raw markup.
      id: 'description', header: 'Description', accessorFn: (row) => stripHtml(row.description),
      enableColumnFilter: false, meta: { flex: '3 1 0', minWidth: 140 },
      cell: ({ getValue }) => (
        <Box title={getValue()} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
          {getValue()}
        </Box>
      ),
    },
  ], [showHumanIds]);

  // Flex spec per column (title/description grow; id/type fixed). Drives both the
  // header and body so they stay aligned even when the panel is narrow.
  const colFlex = (col) => col.columnDef.meta?.flex || `0 0 ${col.columnDef.size}px`;
  const colMinWidth = (col) => col.columnDef.meta?.minWidth || col.columnDef.size;

  const table = useTable({
    features: treeGridFeatures,
    data: items,
    columns,
    state: { sorting, columnFilters, globalFilter, expanded: expandedState },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpandedState,
    getSubRows: (row) => row.children,
    getRowId: (row) => row.id,
    filterFromLeafRows: true,
    autoResetExpanded: false,
    meta: { updateData },
  });

  const rows = table.getRowModel().rows;
  const rowIds = useMemo(() => rows.map((r) => r.id), [rows]);

  // Virtualization over the (filtered/sorted/expanded) visible rows.
  const scrollRef = useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
    // Seed a viewport so rows render before the first measurement (and under
    // jsdom, where the scroll element reports a 0-height rect). Real layout
    // overrides this immediately in the browser.
    initialRect: { width: 800, height: 600 },
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Row id to highlight while dragging: the container a drop would re-parent into.
  const [reparentTargetId, setReparentTargetId] = useState(null);

  const handleDragOver = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) { setReparentTargetId(null); return; }
    const plan = planDrop(items, active.id, over.id);
    setReparentTargetId(plan && plan.kind === 'reparent' ? (plan.payload.new_parent_id ?? null) : null);
  }, [items]);

  const handleDragEnd = useCallback(({ active, over }) => {
    setReparentTargetId(null);
    if (!over) return;
    const plan = planDrop(items, active.id, over.id);
    if (plan && onMoveItem) onMoveItem(plan.id, plan.payload);
  }, [items, onMoveItem]);

  // Anchor for shift-click range selection (last single/ctrl click).
  const anchorIdRef = useRef(null);
  const handleSelect = useCallback((event, row) => {
    if (!onSelectedChange) return;
    if (event.shiftKey && anchorIdRef.current && rowIds.includes(anchorIdRef.current)) {
      // Select the contiguous visible range between the anchor and this row.
      const a = rowIds.indexOf(anchorIdRef.current);
      const b = rowIds.indexOf(row.id);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        onSelectedChange(event, rowIds.slice(lo, hi + 1));
        return;
      }
    }
    if (event.ctrlKey || event.metaKey) {
      const next = selected.includes(row.id)
        ? selected.filter((id) => id !== row.id)
        : [...selected, row.id];
      anchorIdRef.current = row.id;
      onSelectedChange(event, next);
    } else {
      anchorIdRef.current = row.id;
      onSelectedChange(event, [row.id]);
    }
  }, [onSelectedChange, selected, rowIds]);

  const handleRowContextMenu = useCallback((event, row) => {
    if (onContextMenu) onContextMenu(event, row.original);
  }, [onContextMenu]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Typography sx={{ p: 2, color: 'error' }}>{typeof error === 'string' ? error : 'Failed to load tree'}</Typography>;
  }

  const headers = table.getHeaderGroups()[0].headers;
  const virtualRows = rowVirtualizer.getVirtualItems();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="requirements-tree-grid">
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField fullWidth size="small" placeholder="Search items..." value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          slotProps={{ input: { startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }, htmlInput: { 'data-testid': 'global-search' } }} />
        {onRefresh && (
          <IconButton onClick={onRefresh} size="small" title="Refresh tree"><RefreshIcon /></IconButton>
        )}
      </Box>

      <Paper variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', bgcolor: 'action.hover', fontWeight: 600, borderBottom: '2px solid', borderColor: 'divider' }}>
          <Box sx={{ width: 28, flex: '0 0 28px' }} />
          {headers.map((h) => (
            <Box key={h.id} sx={{ flex: colFlex(h.column), minWidth: colMinWidth(h.column), p: 1, display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Box data-testid={`sort-${h.column.id}`} onClick={h.column.getToggleSortingHandler()}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, userSelect: 'none', flex: 1 }}>
                {flexRender(h.column.columnDef.header, h.getContext())}
                {{ asc: <ArrowUpward sx={{ fontSize: 14 }} />, desc: <ArrowDownward sx={{ fontSize: 14 }} /> }[h.column.getIsSorted()] ?? null}
              </Box>
              {h.column.getCanFilter() && <ColumnFilterButton column={h.column} />}
            </Box>
          ))}
        </Box>

        {/* Body (virtualized + sortable) */}
        <Box
          ref={scrollRef}
          data-testid="tree-grid-body"
          role="treegrid"
          aria-label="Requirements"
          tabIndex={0}
          onContextMenu={(e) => {
            // Right-click on empty space (not a row) → background menu.
            if (e.target.closest('[data-rowid]')) return;
            if (onBackgroundContextMenu) { e.preventDefault(); onBackgroundContextMenu(e); }
          }}
          sx={{ flex: 1, overflow: 'auto', position: 'relative' }}
        >
          {rows.length === 0 ? (
            <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              No items found.
            </Typography>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]}
              onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={() => setReparentTargetId(null)}>
              <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
                <Box sx={{ height: rowVirtualizer.getTotalSize(), width: '100%', position: 'relative' }}>
                  {virtualRows.map((virtualRow) => {
                    const row = rows[virtualRow.index];
                    return (
                      <SortableRow key={row.id} row={row} virtualRow={virtualRow}
                        selected={selected} isDropTarget={reparentTargetId === row.id}
                        onSelect={handleSelect} onContextMenu={handleRowContextMenu}>
                        {row.getVisibleCells().map((cell) => (
                          <Box key={cell.id} sx={{ flex: colFlex(cell.column), minWidth: colMinWidth(cell.column), px: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </Box>
                        ))}
                      </SortableRow>
                    );
                  })}
                </Box>
              </SortableContext>
            </DndContext>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

RequirementsTreeGrid.propTypes = {
  items: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  selected: PropTypes.arrayOf(PropTypes.string),
  expanded: PropTypes.arrayOf(PropTypes.string),
  onSelectedChange: PropTypes.func,
  onExpandedChange: PropTypes.func,
  onContextMenu: PropTypes.func,
  onBackgroundContextMenu: PropTypes.func,
  onUpdateItem: PropTypes.func,
  onMoveItem: PropTypes.func,
  onRefresh: PropTypes.func,
};
